import { FC, useEffect, useRef, useState } from 'react';

import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import SVGIcon from 'react-inlinesvg';

import IconThankYou from '@assets/svg/thank-you.svg';
import { Button, Loader } from '@components';
import { postMessage } from '@store';
import { composeClassName, EMAIL_REGEXP } from '@utilities';

import styles from '../index.module.scss';

export type Fields = {
	from_email: string;
	note: string;
};

type Props = {
	onClose: () => void;
	standalone?: boolean;
};

export const QuestionsForm: FC<Readonly<Props>> = ({ onClose, standalone }) => {
	const { t } = useTranslation();
	const [error, setError] = useState(false);
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const timer = useRef<NodeJS.Timeout | null>(null);
	const {
		register,
		handleSubmit,
		formState: { errors }
	} = useForm<Fields>();

	const onFormSubmit: SubmitHandler<Fields> = async (data: Fields) => {
		setLoading(true);

		try {
			await postMessage(data);

			setError(false);
			setSuccess(true);

			timer.current = setTimeout(() => {
				onClose();
				setSuccess(false);
			}, 2000);
		} catch (e) {
			setError(true);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		return () => {
			if (typeof timer.current === 'number') {
				clearTimeout(timer.current);
			}
		};
	}, []);

	return (
		<form
			onSubmit={handleSubmit(onFormSubmit)}
			className={composeClassName(styles.form, standalone ? styles.standalone : '')}
			data-testid="questions-form"
		>
			{!success && <h2>{t('Questions or Suggestions')}</h2>}

			{loading && (
				<div className={styles.loader}>
					<Loader />
				</div>
			)}

			{error && <p className={styles.formError}>{t('Something went wrong.')}</p>}

			{success ? (
				<p className={styles.success}>
					<SVGIcon src={IconThankYou} />

					{t("We received your question. We'll be in touch shortly.")}
				</p>
			) : (
				<>
					<div className={styles.row}>
						<label className={styles.label} htmlFor="email">
							{t('Email')}
						</label>

						<input
							{...register('from_email', { required: true, pattern: EMAIL_REGEXP })}
							id="email"
							className={styles.field}
						/>

						{errors.from_email && <p className={styles.error}>{t('Please use a valid email address.')}</p>}
					</div>

					<div className={styles.row}>
						<label className={styles.label} htmlFor="question">
							{t('Your question')}
						</label>

						<textarea {...register('note', { required: true })} id="question" className={styles.textarea} />

						{errors.note && <p className={styles.error}>{t('The "question" field is required.')}</p>}
					</div>

					<div className={styles.actions}>
						<Button type="button" outline={true} onClick={onClose}>
							{t('Cancel')}
						</Button>

						<Button type="submit">{t('Send')}</Button>
					</div>
				</>
			)}
		</form>
	);
};

export default QuestionsForm;
